/**
 * Shows a connection (or one of its tree nodes) in the Object Explorer from outside the tree — the Home card and the Connections dialog (round B U6): opens the
 * sidebar and its Explorer panel when collapsed, expands the connection node, selects the target and highlights it —
 * the highlight scrolls it into view (`TreeNode`'s `shouldScrollIntoView`). The connection part is the toolbar's own
 * `syncExplorerSelection`.
 *
 * C2 N20: a storage service folder (the Home card's Blob Containers / Tables / … actions) is also OPENED — expanded and
 * listed, as a click on its chevron would — so the action shows the containers, not a highlighted collapsed row that
 * looked like nothing happened.
 */
import type { Connection } from '../../../types/electron';
import { useDatabaseStore } from '../../database-objects/store/databaseStore';
import { useSidebarStore } from '../../database-objects/store/sidebarStore';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { loadStorageServiceFolder } from '../../database-objects/services/azureStorageListing';
import { getStorageTreeQueryClient } from '../../database-objects/services/storageConnectionTree';
import { syncExplorerSelection } from '../../toolbar-context/services/explorerSyncService';
import { findNodeById } from '../services/treeService';
import type { StorageServiceFolderNode } from '../models/nodeTypes';
import { createConnectionNodeId } from './nodeIdUtils';
import { safeConnectionName, safeConnectionServer } from '../../../types/safeConnectionDisplay';

function openStorageServiceFolder(nodeId: string): void {
  const store = useDatabaseStore.getState();
  const node = findNodeById(nodeId, store.treeData);
  if (!node || node.type !== 'storage-service-folder' || node.isExpanded) return;
  store.updateNode(nodeId, { isExpanded: true });
  void loadStorageServiceFolder({ node: node as StorageServiceFolderNode, queryClient: getStorageTreeQueryClient(), updateNode: useDatabaseStore.getState().updateNode });
}

export function revealInObjectExplorer(connection: Connection, nodeId: string): void {
  const sidebar = useSidebarStore.getState();
  if (sidebar.isCollapsed) sidebar.toggleCollapsed();
  if (sidebar.explorerCollapsed) sidebar.toggleExplorerCollapsed();
  syncExplorerSelection({
    context: { connectionId: connection.id, connectionName: safeConnectionName(connection), serverHost: safeConnectionServer(connection), engine: connection.engine ?? 'azurestorage' },
    skipIfAlreadySelected: false,
  });
  if (nodeId === createConnectionNodeId(connection.id)) return;
  openStorageServiceFolder(nodeId);
  const ui = useDatabaseUIStore.getState();
  ui.setSelectedNodeId(nodeId);
  ui.setHighlightedNodeId(nodeId);
}
